import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";

const MeasurementRunForm = ({ sensors, devices, measurementTypes, selectedMeasurement, showModal, setShowModal, onSave, auth, onLogout }) => {
    const [formData, setFormData] = useState({
        device_id: "",
        measurement_type_id: "",
        stations: [],
        run_description: "",
        sensors_with_channels: [],
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Station options from the database schema
    const stationOptions = ['1', '2', '3'];

    useEffect(() => {
        if (selectedMeasurement) {
            // For editing
            setFormData({
				id: selectedMeasurement.id,
                device_id: selectedMeasurement.device_id ?? "",
				stations: selectedMeasurement.stations,
                measurement_type_id: selectedMeasurement.measurement_type_id ?? "",
                run_description: selectedMeasurement.run_description,
                sensors_with_channels: selectedMeasurement.sensors_with_channels?.map(sc => ({
					id: sc.id,
                    sensor_id: sc.sensor_id,
                    channel: sc.channel
                })),
            });
        } else {
            // For new measurement
            setFormData({
                device_id: "",
                measurement_type_id: "",
                stations: [],
                run_description: "",
                sensors_with_channels: []
            });
        }
    }, [selectedMeasurement]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleStationChange = (station) => {
        setFormData(prev => {
            const newStations = prev.stations.includes(station)
                ? prev.stations.filter(s => s !== station)
                : [...prev.stations, station];

            return {
                ...prev,
                stations: newStations
            };
        });
    };

    const handleSensorChannelChange = (index, field, value) => {
        setFormData(prev => {
            const updatedSensors = [...prev.sensors_with_channels];
            updatedSensors[index] = {
                ...updatedSensors[index],
                [field]: field === 'sensor_id' ? parseInt(value) : value
            };

            return {
                ...prev,
                sensors_with_channels: updatedSensors
            };
        });
    };

    const addSensorChannel = () => {
        setFormData(prev => ({
            ...prev,
            sensors_with_channels: [
                ...prev.sensors_with_channels,
                { sensor_id: "", channel: "" }
            ]
        }));
    };

    const removeSensorChannel = (index) => {
        setFormData(prev => ({
            ...prev,
            sensors_with_channels: prev.sensors_with_channels.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            // Expect data as in the backend format
            const measurementData = {
                device_id: parseInt(formData.device_id),
                measurement_type_id: parseInt(formData.measurement_type_id),
                stations: formData.stations,
                run_description: formData.run_description,
                sensors_with_channels: formData.sensors_with_channels.map(sc => ({
					id: sc.id,
                    sensor_id: parseInt(sc.sensor_id),
                    channel: sc.channel.toString()
                })),
                ...(formData.id && { id: formData.id }) // Only include ID for editin
            };

            await onSave(measurementData);
            setShowModal(false);
        } catch (err) {
            setError(err.message || "Failed to save measurement run.");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setShowModal(false);
    };

    return (
        <Modal show={showModal} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {selectedMeasurement ? "Edit Measurement Run" : "Add New Measurement Run"}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Device</Form.Label>
                                <Form.Select
                                    name="device_id"
                                    value={formData.device_id}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Device</option>
                                    {devices.map(device => (
                                        <option key={device.id} value={device.id}>
                                            { `Device ${device.device_id}`}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Measurement Type</Form.Label>
                                {measurementTypes.length && (
                                    <Form.Select
                                        name="measurement_type_id"
                                        value={formData.measurement_type_id}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Type</option>
                                        {measurementTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.type_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="run_description"
                                    value={formData.run_description}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Label>Stations</Form.Label>
                            <div className="d-flex gap-3">
                                {stationOptions.map(station => (
                                    <Form.Check
                                        key={station}
                                        type="checkbox"
                                        id={`station-${station}`}
                                        label={`Station ${station}`}
                                        checked={formData.stations.includes(station)}
                                        onChange={() => handleStationChange(station)}
                                    />
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <hr />
                    <h5>Sensors & Channels</h5>
                    {formData.sensors_with_channels.map((sc, index) => (
                        <Row key={index} className="mb-2 align-items-end">
                            <Col md={5}>
                                <Form.Group>
                                    <Form.Label>Sensor</Form.Label>
                                    <Form.Select
                                        value={sc.sensor_id}
                                        onChange={(e) =>
                                            handleSensorChannelChange(index, "sensor_id", e.target.value)
                                        }
                                        required
                                    >
                                        <option value="">Select Sensor</option>
                                        {sensors.map(sensor => (
                                            <option key={sensor.id} value={sensor.id}>
                                                {sensor.serial_number} (ID: {sensor.id})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={5}>
                                <Form.Group>
                                    <Form.Label>Channel</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={sc.channel}
                                        onChange={(e) =>
                                            handleSensorChannelChange(index, "channel", e.target.value)
                                        }
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Button
                                    variant="danger"
                                    onClick={() => removeSensorChannel(index)}
                                    className="w-100"
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    ))}
                    <Button
                        variant="outline-primary"
                        onClick={addSensorChannel}
                        className="mb-3 mt-4"
                    >
                        Add Sensor & Channel
                    </Button>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default MeasurementRunForm;